import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useApp, useCloudAuth, useCloudSummary, useCloudSync, useSession, useUi } from '../app/context';
import { openAccount, patchUi, ui } from '../app/ui';
import { MIN_PASSWORD, type AuthResult } from '../cloud/auth';
import { relativeTime } from '../cloud/summary';
import type { MoveReport } from '../app/controller';
import { useStore } from '../core/store';
import { splitName, type DocMeta } from '../core/documents';
import { AlertIcon, ArrowLeftIcon, CheckIcon, CloseIcon, CloudIcon, EyeIcon, MailIcon, MarkdownIcon, SearchIcon, TextIcon, TrashIcon } from './icons';
import { usePresence } from './Popover';
import { WordPaths } from './Brand';
import { lookOf, SyncGlyph } from './SyncIndicator';

/**
 * The account, as one calm object that changes shape with what it holds.
 *
 * Signed out it is a small sign-in card: the wordmark, one question at a
 * time (sign in · create an account by invitation · reset a password), and
 * a clear "check your inbox" when a link is on its way. Signed in it becomes
 * a compact account center: who you are and how the cloud stands at the top,
 * then three places — the Library, what is still only on This Device, and
 * the Account itself.
 *
 * It is never a requirement: MyTXT works fully without it, and it says so.
 * On a phone it is a sheet that rises from the bottom edge.
 */

export function CloudDialog() {
  const open = useUi((s) => s.account);
  const auth = useCloudAuth();
  // Arriving through an email link is a reason to show the account at once: a recovery link needs its form,
  // a bad link needs its explanation.
  const arrived = auth.status === 'recovery' || (auth.status === 'signed-out' && !!auth.notice);
  useEffect(() => {
    if (arrived) openAccount();
  }, [arrived]);
  const { mounted, closing } = usePresence(open, 220);
  const [returnTo, setReturnTo] = useState<HTMLElement | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReturnTo(document.activeElement as HTMLElement | null);
  }
  if (!mounted) return null;
  return <Surface closing={closing} returnTo={returnTo} />;
}

const FOCUSABLE = 'button:not([disabled]):not([inert] *), input:not([disabled]), a[href], select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Surface({ closing, returnTo }: { closing: boolean; returnTo: HTMLElement | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const auth = useCloudAuth();
  const compact = useUi((s) => s.compact);

  const close = () => {
    patchUi({ account: false });
    requestAnimationFrame(() => returnTo?.isConnected && returnTo.focus({ preventScroll: true }));
  };

  useEffect(() => {
    if (closing) return;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>('.acct-body ' + FOCUSABLE.split(',')[0]);
    first?.focus({ preventScroll: true });
  }, [closing, auth.status]);

  // Escape works wherever focus has gone (a control that was disabled while working may drop it).
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || ui.get().dialog) return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const keyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = (i + (e.shiftKey ? -1 : 1) + items.length) % items.length;
    e.preventDefault();
    items[next]?.focus();
  };

  const label = auth.status === 'recovery' ? 'Choose a new password' : 'MyTXT Account';

  return (
    <div className="acct-layer" data-state={closing ? 'closing' : 'open'} data-compact={compact || undefined} onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div ref={ref} className="acct" role="dialog" aria-modal="true" aria-label={label} data-status={auth.status} onKeyDown={keyDown}>
        {compact && <Grabber onDismiss={close} />}
        <button type="button" className="acct-close" aria-label="Close" onClick={close}>
          <CloseIcon size={14} />
        </button>
        <Morph>
          <Body onClose={close} />
        </Morph>
      </div>
    </div>
  );
}

/** On a phone the sheet can be pulled down to put it away; a short pull springs back. */
function Grabber({ onDismiss }: { onDismiss: () => void }) {
  const down = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget.closest<HTMLElement>('.acct');
    if (!el) return;
    const y0 = e.clientY;
    let dy = 0;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    el.style.transition = 'none';
    el.style.animation = 'none';
    const move = (ev: PointerEvent) => {
      dy = Math.max(0, ev.clientY - y0);
      el.style.transform = `translateY(${dy}px)`;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      el.style.transition = 'transform 320ms var(--optic)';
      if (dy > Math.min(140, el.offsetHeight * 0.25)) {
        el.style.transform = 'translateY(100%)';
        onDismiss();
      } else el.style.transform = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  return (
    <div className="acct-grab" aria-hidden="true" onPointerDown={down}>
      <span className="acct-grabber" />
    </div>
  );
}

/** The card follows its content's height with a short spring instead of jumping. */
function Morph({ children }: { children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (!o || !i) return;
    let first = true;
    const ro = new ResizeObserver(() => {
      const h = i.offsetHeight;
      if (first) {
        first = false;
        o.style.height = `${h}px`;
        return;
      }
      o.style.height = `${h}px`;
    });
    ro.observe(i);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={outer} className="acct-morph">
      <div ref={inner} className="acct-body">
        {children}
      </div>
    </div>
  );
}

function Body({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const auth = useCloudAuth();
  if (!app.cloud.configured) {
    return (
      <div className="acct-pane acct-center-pane">
        <Emblem tone="quiet">
          <CloudIcon size={26} />
        </Emblem>
        <h2 className="acct-title">Writing stays on this device</h2>
        <p className="acct-lede">Cloud accounts aren’t set up in this copy of MyTXT. Everything you write is kept here, and Save writes real files.</p>
        {app.cloud.problem && (
          <p className="acct-message" data-tone="error" role="alert">
            <AlertIcon size={15} />
            <span>{app.cloud.problem}</span>
          </p>
        )}
      </div>
    );
  }
  if (auth.status === 'loading') {
    return (
      <div className="acct-pane acct-center-pane" role="status">
        <span className="acct-spinner" aria-hidden="true" />
        <p className="acct-lede">Checking your account…</p>
      </div>
    );
  }
  if (auth.status === 'recovery') {
    return (
      <div className="acct-pane">
        <AuthHeader title="Choose a new password" lede="You followed a recovery link. Choose a new password to finish signing in." />
        <PasswordForm autofocus />
      </div>
    );
  }
  if (auth.status === 'signed-in') return <AccountCenter onClose={onClose} />;
  return <SignedOut />;
}

function Emblem({ children, tone = 'accent' }: { children: ReactNode; tone?: 'accent' | 'quiet' | 'ok' }) {
  return (
    <span className="acct-emblem" data-tone={tone} aria-hidden="true">
      {children}
    </span>
  );
}

function AuthHeader({ title, lede, badge }: { title: string; lede?: ReactNode; badge?: string }) {
  return (
    <header className="acct-auth-head">
      <span className="acct-wordmark" aria-hidden="true">
        <WordPaths />
      </span>
      {badge && <span className="acct-badge">{badge}</span>}
      <h2 className="acct-title">{title}</h2>
      {lede && <p className="acct-lede">{lede}</p>}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Signed out

type Mode = 'sign-in' | 'register' | 'recover';

const COPY: Record<Mode, { title: string; lede: string; submit: string; busy: string }> = {
  'sign-in': { title: 'Welcome back', lede: 'Sign in to keep your writing in sync across your devices.', submit: 'Sign In', busy: 'Signing In…' },
  register: { title: 'Create your account', lede: 'MyTXT cloud is a private beta. Registration is by invitation: an address that hasn’t been invited can’t store documents.', submit: 'Create Account', busy: 'Creating…' },
  recover: { title: 'Reset your password', lede: 'Enter your email address and we’ll send you a link to choose a new password.', submit: 'Send Reset Link', busy: 'Sending…' },
};

function SignedOut() {
  const app = useApp();
  const auth = useCloudAuth();
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AuthResult | null>(null);
  const [sent, setSent] = useState<{ kind: 'register' | 'recover'; message: string } | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const fieldId = useId();
  const service = app.cloud.auth;
  const copy = COPY[mode];

  const change = (next: Mode) => {
    setMode(next);
    setResult(null);
    setSent(null);
    setNeedsConfirmation(false);
    service.dismissNotice();
    requestAnimationFrame(() => document.querySelector<HTMLElement>('.acct [data-autofocus]')?.focus({ preventScroll: true }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setResult(null);
    setNeedsConfirmation(false);
    try {
      const r = mode === 'sign-in' ? await service.signIn(email, password) : mode === 'register' ? await service.signUp(email, password) : await service.requestReset(email);
      if (r.ok) setPassword('');
      if (r.ok && mode !== 'sign-in') setSent({ kind: mode, message: r.message });
      else setResult(r);
      if (!r.ok && mode === 'sign-in' && /confirm your email/i.test(r.message)) setNeedsConfirmation(true);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    setResult(await service.resendConfirmation(email));
    setBusy(false);
  };

  if (sent) {
    return (
      <div className="acct-pane acct-center-pane" key="sent">
        <Emblem tone="ok">
          <MailIcon size={26} />
        </Emblem>
        <h2 className="acct-title">Check your inbox</h2>
        <p className="acct-lede" role="status">
          {sent.message}
        </p>
        {email && <p className="acct-address">{email}</p>}
        <div className="acct-stack">
          <button type="button" className="acct-btn" data-tone="primary" data-wide onClick={() => change('sign-in')}>
            Back to Sign In
          </button>
        </div>
        <p className="acct-fine">The link opens MyTXT in this browser. Didn’t arrive? Check spam, or try again in a minute.</p>
      </div>
    );
  }

  return (
    <div className="acct-pane" key={mode} data-mode={mode}>
      {mode !== 'sign-in' && (
        <button type="button" className="acct-back" onClick={() => change('sign-in')}>
          <ArrowLeftIcon size={15} />
          <span>Sign in instead</span>
        </button>
      )}
      <AuthHeader title={copy.title} lede={copy.lede} badge={mode === 'register' ? 'By invitation' : undefined} />

      {auth.notice && (
        <p className="acct-message" data-tone={auth.notice.tone} role={auth.notice.tone === 'error' ? 'alert' : 'status'}>
          {auth.notice.tone === 'error' ? <AlertIcon size={15} /> : <CheckIcon size={15} />}
          <span>{auth.notice.text}</span>
        </p>
      )}
      {auth.expired && mode === 'sign-in' && !result && !auth.notice && (
        <p className="acct-message" data-tone="info">
          <AlertIcon size={15} />
          <span>Your session ended. Sign in to resume syncing — your writing is safe on this device.</span>
        </p>
      )}

      <form className="acct-form" onSubmit={submit} noValidate>
        <div className="acct-field">
          <label htmlFor={`${fieldId}-email`}>Email</label>
          <input
            id={`${fieldId}-email`}
            data-autofocus
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {mode !== 'recover' && (
          <div className="acct-field">
            <div className="acct-field-row">
              <label htmlFor={`${fieldId}-password`}>Password</label>
              {mode === 'sign-in' && (
                <button type="button" className="acct-link" onClick={() => change('recover')}>
                  Forgot password?
                </button>
              )}
            </div>
            <SecretInput
              id={`${fieldId}-password`}
              name="password"
              autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
              minLength={mode === 'register' ? MIN_PASSWORD : undefined}
              value={password}
              onChange={setPassword}
              describedBy={mode === 'register' ? `${fieldId}-hint` : undefined}
            />
            {mode === 'register' && (
              <small id={`${fieldId}-hint`} className="acct-hint">
                At least {MIN_PASSWORD} characters.
              </small>
            )}
          </div>
        )}
        {result && <Result result={result} />}
        <div className="acct-stack">
          <button type="submit" className="acct-btn" data-tone="primary" data-wide data-busy={busy || undefined} aria-disabled={busy || undefined}>
            {busy && <span className="acct-spinner" data-small aria-hidden="true" />}
            <span>{busy ? copy.busy : copy.submit}</span>
          </button>
          {needsConfirmation && (
            <button type="button" className="acct-btn" data-wide onClick={resend} aria-disabled={busy || undefined}>
              Resend Confirmation Email
            </button>
          )}
        </div>
      </form>

      {mode === 'sign-in' && (
        <p className="acct-switch-line">
          <span>Have an invitation?</span>
          <button type="button" className="acct-link" onClick={() => change('register')}>
            Create account
          </button>
        </p>
      )}
      <p className="acct-fine">No account needed to write. Without one, MyTXT works exactly as before and your documents stay on this device.</p>
    </div>
  );
}

function SecretInput({ id, name, autoComplete, minLength, value, onChange, describedBy, autofocus = false }: { id: string; name?: string; autoComplete: string; minLength?: number; value: string; onChange: (v: string) => void; describedBy?: string; autofocus?: boolean }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="acct-secret">
      <input
        id={id}
        data-autofocus={autofocus || undefined}
        type={shown ? 'text' : 'password'}
        name={name}
        autoComplete={autoComplete}
        autoCapitalize="none"
        spellCheck={false}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={describedBy}
        required
      />
      <button type="button" className="acct-reveal" aria-pressed={shown} aria-label={shown ? 'Hide characters' : 'Show characters'} onClick={() => setShown(!shown)}>
        <EyeIcon size={15} />
      </button>
    </div>
  );
}

function Result({ result }: { result: AuthResult }) {
  return (
    <p className="acct-message" data-tone={result.ok ? 'ok' : 'error'} role={result.ok ? 'status' : 'alert'}>
      {result.ok ? <CheckIcon size={15} /> : <AlertIcon size={15} />}
      <span>{result.message}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Password (recovery link, or "Change Password")

function PasswordForm({ onDone, autofocus = false }: { onDone?: () => void; autofocus?: boolean }) {
  const app = useApp();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AuthResult | null>(null);
  const fieldId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (password !== again) return setResult({ ok: false, message: 'The two passwords don’t match.' });
    setBusy(true);
    const r = await app.cloud.auth.setPassword(password);
    setResult(r);
    setBusy(false);
    if (r.ok) {
      setPassword('');
      setAgain('');
      onDone?.();
    }
  };

  return (
    <form className="acct-form" onSubmit={submit} noValidate>
      <div className="acct-field">
        <label htmlFor={`${fieldId}-new`}>New password</label>
        <SecretInput id={`${fieldId}-new`} autofocus={autofocus} autoComplete="new-password" minLength={MIN_PASSWORD} value={password} onChange={setPassword} describedBy={`${fieldId}-hint`} />
        <small id={`${fieldId}-hint`} className="acct-hint">
          At least {MIN_PASSWORD} characters.
        </small>
      </div>
      <div className="acct-field">
        <label htmlFor={`${fieldId}-again`}>Repeat new password</label>
        <input id={`${fieldId}-again`} type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
      </div>
      {result && <Result result={result} />}
      <div className="acct-stack">
        <button type="submit" className="acct-btn" data-tone="primary" data-wide data-busy={busy || undefined} aria-disabled={busy || undefined}>
          {busy && <span className="acct-spinner" data-small aria-hidden="true" />}
          <span>{busy ? 'Saving…' : 'Save Password'}</span>
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Signed in: the account center

type Place = 'library' | 'device' | 'account';

function AccountCenter({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const auth = useCloudAuth();
  const sync = useCloudSync();
  const summary = useCloudSummary();
  const autosave = useUi((s) => s.prefs.cloudAutosave);
  const docs = useSession((s) => s.docs);
  const [place, setPlace] = useState<Place>('library');
  const [syncing, setSyncing] = useState(false);
  // Read once per opening; "last synced" need not tick.
  const [now] = useState(() => Date.now());
  const denied = sync?.access === 'denied';
  const email = auth.user?.email ?? '';
  const localCount = docs.filter((d) => !d.cloud).length;
  const look = lookOf(summary, autosave, 0);

  const syncNow = async () => {
    if (syncing || !sync) return;
    setSyncing(true);
    try {
      await app.cloud.syncNow();
    } finally {
      setSyncing(false);
    }
  };

  const places: Array<{ id: Place; label: string; count?: number }> = [
    { id: 'library', label: 'Library', count: sync?.library?.length },
    { id: 'device', label: 'This Device', count: localCount || undefined },
    { id: 'account', label: 'Account' },
  ];

  const tabKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = places.findIndex((p) => p.id === place);
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = places[(i + step + places.length) % places.length].id;
    setPlace(next);
    e.currentTarget.querySelector<HTMLElement>(`[data-place="${next}"]`)?.focus();
  };

  return (
    <div className="acct-pane acct-center">
      <header className="acct-id">
        <span className="acct-avatar" aria-hidden="true">
          {(email[0] ?? '?').toUpperCase()}
        </span>
        <span className="acct-id-text">
          <span className="acct-email" data-testid="account-email">
            {email}
          </span>
          <span className="acct-state" data-tone={summary.tone} role="status" aria-live="polite">
            <SyncGlyph look={look} size={15} />
            <span data-testid="sync-status" data-state={summary.state}>
              {summary.label}
            </span>
          </span>
        </span>
      </header>
      <div className="acct-sync-row">
        <span className="acct-id-detail">{summary.detail ?? (sync?.lastSyncedAt ? `Last synced ${relativeTime(sync.lastSyncedAt, Math.max(now, sync.lastSyncedAt))}` : '')}</span>
        <button type="button" className="acct-btn" data-size="small" onClick={syncNow} aria-disabled={syncing || undefined} disabled={!sync}>
          {syncing ? 'Syncing…' : denied ? 'Check Again' : sync?.blocked === 'auth' ? 'Reconnect' : 'Sync Now'}
        </button>
      </div>

      {denied ? (
        <>
          <p className="acct-message" data-tone="error" role="alert">
            <AlertIcon size={15} />
            <span>This account isn’t on the private-beta list, so cloud storage is unavailable. If you were invited, check that you confirmed the invited address. Your documents stay on this device.</span>
          </p>
          <AccountPlace onClose={onClose} />
        </>
      ) : (
        <>
          <div className="acct-places" role="tablist" aria-label="Account" onKeyDown={tabKeys}>
            {places.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                data-place={p.id}
                aria-selected={place === p.id}
                tabIndex={place === p.id ? 0 : -1}
                className="acct-place"
                onClick={() => setPlace(p.id)}
              >
                {p.label}
                {p.count !== undefined && <span className="acct-count">{p.count}</span>}
              </button>
            ))}
            <span className="acct-places-thumb" data-at={places.findIndex((p) => p.id === place)} aria-hidden="true" />
          </div>
          <div className="acct-place-body" role="tabpanel" key={place}>
            {place === 'library' && <LibraryPlace onClose={onClose} />}
            {place === 'device' && <DevicePlace onDone={() => setPlace('library')} />}
            {place === 'account' && <AccountPlace onClose={onClose} />}
          </div>
        </>
      )}
    </div>
  );
}

function KindIcon({ name }: { name: string }) {
  return splitName(name).ext.toLowerCase() === '.txt' ? <TextIcon size={16} /> : <MarkdownIcon size={16} />;
}

function LibraryPlace({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const sync = useCloudSync();
  const unsynced = useStore(app.cloud.unsynced, (s) => s);
  const docs = useSession((s) => s.docs);
  const open = new Set(docs.map((d) => d.id));
  const library = sync?.library ?? null;
  const [query, setQuery] = useState('');
  // Relative times are read once per opening; they need not tick.
  const [now] = useState(() => Date.now());
  const sorted = library ? [...library].sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at)) : null;
  const q = query.trim().toLowerCase();
  const shown = sorted && q ? sorted.filter((d) => d.name.toLowerCase().includes(q)) : sorted;

  return (
    <>
      {unsynced.length > 0 && (
        <section className="acct-group" aria-label="Waiting to sync">
          <h3 className="acct-h">Closed, still waiting to sync</h3>
          <p className="acct-hint">These have words the cloud doesn’t have yet. They are kept on this device and sync when they can.</p>
          <ul className="acct-list">
            {unsynced.map((d) => (
              <li key={d.id} className="acct-item" data-waiting>
                <span className="acct-item-icon">
                  <KindIcon name={d.name} />
                </span>
                <span className="acct-item-name">{d.name}</span>
                <button type="button" className="acct-pill" onClick={() => void app.cloud.open(d.id)}>
                  Reopen
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {library && library.length > 7 && (
        <label className="acct-search">
          <SearchIcon size={15} />
          <input type="search" placeholder="Find in your library" aria-label="Find in your library" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
      )}

      {!library && sync?.libraryStatus !== 'error' && (
        <ul className="acct-list" aria-busy="true" aria-label="Loading your library">
          {[0, 1, 2].map((i) => (
            <li key={i} className="acct-item acct-skeleton" style={{ animationDelay: `${i * 90}ms` }} />
          ))}
        </ul>
      )}
      {sync?.libraryStatus === 'error' && !library && (
        <p className="acct-message" data-tone="info" role="alert">
          <AlertIcon size={15} />
          <span>The library couldn’t be loaded. It will retry; your documents are not affected.</span>
        </p>
      )}
      {library && library.length === 0 && (
        <div className="acct-empty">
          <Emblem tone="quiet">
            <CloudIcon size={24} />
          </Emblem>
          <p className="acct-empty-title">Your library is empty</p>
          <p className="acct-hint">New documents you write while signed in with Cloud Autosave on appear here. Existing ones can move in from This Device.</p>
        </div>
      )}
      {shown && shown.length === 0 && library && library.length > 0 && <p className="acct-hint acct-none">Nothing matches “{query}”.</p>}
      {shown && shown.length > 0 && (
        <ul className="acct-list" aria-label="Library">
          {shown.map((d) => {
            const isOpen = open.has(d.id);
            return (
              <li key={d.id} className="acct-item" data-open={isOpen || undefined}>
                <span className="acct-item-icon">
                  <KindIcon name={d.name} />
                </span>
                <span className="acct-item-text">
                  <span className="acct-item-name">{d.name}</span>
                  <span className="acct-item-meta">{isOpen ? 'Open now' : `Edited ${relativeTime(Date.parse(d.updated_at), now)}`}</span>
                </span>
                <button
                  type="button"
                  className="acct-icon-btn"
                  data-danger
                  aria-label={`Delete ${d.name} from the cloud`}
                  data-tip="Delete from Cloud…"
                  onClick={() => {
                    onClose();
                    void app.cloud.deleteFromCloud(d.id);
                  }}
                >
                  <TrashIcon size={15} />
                </button>
                <button
                  type="button"
                  className="acct-pill"
                  data-tone={isOpen ? undefined : 'primary'}
                  onClick={async () => {
                    if (await app.cloud.open(d.id)) onClose();
                  }}
                >
                  {isOpen ? 'Show' : 'Open'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function DevicePlace({ onDone }: { onDone: () => void }) {
  const app = useApp();
  const docs = useSession((s) => s.docs);
  const local = docs.filter((d) => !d.cloud);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState<MoveReport[] | null>(null);

  const toggle = (id: string) =>
    setChosen((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const all = local.length > 0 && local.every((d) => chosen.has(d.id));

  const move = async () => {
    if (busy || !chosen.size) return;
    setBusy(true);
    setReports(null);
    try {
      setReports(await app.cloud.moveToCloud([...chosen]));
      setChosen(new Set());
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {reports && (
        <section className="acct-group" aria-label="Result">
          <ul className="acct-list">
            {reports.map((r) => (
              <li key={r.id} className="acct-item" data-status={r.status}>
                <span className="acct-item-icon">{r.status === 'verified' ? <CheckIcon size={15} /> : <AlertIcon size={15} />}</span>
                <span className="acct-item-text">
                  <span className="acct-item-name">{r.name}</span>
                  <span className="acct-item-meta">{r.detail}</span>
                </span>
              </li>
            ))}
          </ul>
          {reports.some((r) => r.status === 'verified') && (
            <button type="button" className="acct-link" onClick={onDone}>
              See them in your Library
            </button>
          )}
        </section>
      )}
      {local.length === 0 ? (
        !reports && (
          <div className="acct-empty">
            <Emblem tone="ok">
              <CheckIcon size={22} />
            </Emblem>
            <p className="acct-empty-title">Nothing left behind</p>
            <p className="acct-hint">Every open document is in your cloud library.</p>
          </div>
        )
      ) : (
        <section className="acct-group" aria-label="On this device only">
          <div className="acct-row">
            <h3 className="acct-h">Open, on this device only</h3>
            <button type="button" className="acct-link" onClick={() => setChosen(all ? new Set() : new Set(local.map((d) => d.id)))}>
              {all ? 'Select None' : 'Select All'}
            </button>
          </div>
          <ul className="acct-list">
            {local.map((d: DocMeta) => (
              <li key={d.id} className="acct-item" data-chosen={chosen.has(d.id) || undefined}>
                <label className="acct-check">
                  <input type="checkbox" checked={chosen.has(d.id)} onChange={() => toggle(d.id)} />
                  <span className="acct-checkbox" aria-hidden="true">
                    <CheckIcon size={12} />
                  </span>
                  <span className="acct-item-icon">
                    <KindIcon name={d.name} />
                  </span>
                  <span className="acct-item-name">{d.name}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="acct-hint">Nothing is uploaded unless you choose it. Each document is read back from the cloud to confirm it arrived intact; your copy here is never removed.</p>
          <div className="acct-stack">
            <button type="button" className="acct-btn" data-tone="primary" data-wide data-busy={busy || undefined} aria-disabled={busy || undefined} disabled={chosen.size === 0} onClick={move}>
              {busy && <span className="acct-spinner" data-small aria-hidden="true" />}
              <span>{busy ? 'Moving…' : chosen.size ? `Move ${chosen.size} to Cloud` : 'Move to Cloud'}</span>
            </button>
          </div>
        </section>
      )}
    </>
  );
}

function AccountPlace({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const sync = useCloudSync();
  const autosave = useUi((s) => s.prefs.cloudAutosave);
  const [changingPassword, setChangingPassword] = useState(false);
  const switchId = useId();
  const denied = sync?.access === 'denied';

  return (
    <>
      {!denied && (
        <section className="acct-group acct-card">
          <label className="acct-switch" htmlFor={switchId}>
            <span className="acct-switch-text">
              <span>Cloud Autosave</span>
              <small>{autosave ? 'Changes upload shortly after you pause writing.' : 'Off: changes wait for Sync Now. Drafts are still kept on this device.'}</small>
            </span>
            <input id={switchId} type="checkbox" role="switch" className="acct-toggle" checked={autosave} onChange={(e) => app.setPrefs({ cloudAutosave: e.target.checked })} />
          </label>
          <p className="acct-hint">Save and Save As still write real files. The cloud is a separate, private copy.</p>
        </section>
      )}
      <section className="acct-group acct-card">
        {changingPassword ? (
          <>
            <div className="acct-row">
              <h3 className="acct-h">Change password</h3>
              <button type="button" className="acct-link" onClick={() => setChangingPassword(false)}>
                Cancel
              </button>
            </div>
            <PasswordForm autofocus onDone={() => setChangingPassword(false)} />
          </>
        ) : (
          <button type="button" className="acct-row-btn" onClick={() => setChangingPassword(true)}>
            <span>Change Password…</span>
          </button>
        )}
        <button
          type="button"
          className="acct-row-btn"
          data-tone="danger"
          onClick={() => {
            // The sign-out question (if any) is a sheet in the window; it needs the dialog out of the way.
            onClose();
            void app.cloud.signOut();
          }}
        >
          <span>Sign Out</span>
          <small>On this device only</small>
        </button>
      </section>
    </>
  );
}
